// apps/web/src/components/astral/astralViewportUtils.ts

export interface BoundingBox {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  width: number;
  height: number;
  centerX: number;
  centerY: number;
}

/**
 * Calcula o retângulo delimitador (bounding box) de todos os nós posicionados.
 */
export function computeNodesBoundingBox(
  positions: Record<string, { x: number; y: number; width: number; height: number }>,
): BoundingBox | null {
  const posArray = Object.values(positions);
  if (posArray.length === 0) return null;

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  posArray.forEach((p) => {
    if (p.x < minX) minX = p.x;
    if (p.x + p.width > maxX) maxX = p.x + p.width;
    if (p.y < minY) minY = p.y;
    if (p.y + p.height > maxY) maxY = p.y + p.height;
  });

  if (!Number.isFinite(minX) || !Number.isFinite(maxX)) return null;

  return {
    minX,
    maxX,
    minY,
    maxY,
    width: maxX - minX,
    height: maxY - minY,
    centerX: (minX + maxX) / 2,
    centerY: (minY + maxY) / 2,
  };
}

/**
 * Calcula o pan e zoom ótimos para enquadrar perfeitamente a árvore no viewport atual.
 * Trata telas mobile (320px - 768px), tablets e monitores desktop com respiros proporcionais.
 */
export function computeFitViewport({
  containerWidth,
  containerHeight,
  bbox,
  rootPos,
}: {
  containerWidth: number;
  containerHeight: number;
  bbox: BoundingBox | null;
  rootPos?: { x: number; y: number; width: number; height: number };
}): { panX: number; panY: number; zoom: number } {
  if (containerWidth <= 0 || containerHeight <= 0) {
    return { panX: 0, panY: 0, zoom: 1 };
  }

  const isMobile = containerWidth < 768;

  // Se houver apenas o nó raiz ou nenhum nó
  if (!bbox || (bbox.width <= 0 && bbox.height <= 0)) {
    const root = rootPos || { x: 0, y: 0, width: 220, height: 52 };
    const z = isMobile ? 0.9 : 1.0;
    return {
      panX: Math.round(containerWidth / 2 - (root.x + root.width / 2) * z),
      panY: Math.round(containerHeight / 2 - (root.y + root.height / 2) * z),
      zoom: z,
    };
  }

  // Margens de respiro confortáveis
  const paddingX = isMobile ? 36 : 120;
  const paddingY = isMobile ? 54 : 120;

  const contentWidth = Math.max(80, bbox.width + paddingX);
  const contentHeight = Math.max(80, bbox.height + paddingY);

  const scaleX = containerWidth / contentWidth;
  const scaleY = containerHeight / contentHeight;

  // Zoom mínimo e máximo para manter a legibilidade sem truncar o conteúdo
  const minZoomLimit = isMobile ? 0.20 : 0.35;
  const maxZoomLimit = isMobile ? 1.05 : 1.20;
  const rawFitZoom = Math.min(scaleX, scaleY);
  const fitZoom = Math.min(maxZoomLimit, Math.max(minZoomLimit, rawFitZoom));

  const targetPanX = Math.round(containerWidth / 2 - bbox.centerX * fitZoom);
  const targetPanY = Math.round(containerHeight / 2 - bbox.centerY * fitZoom);

  return {
    panX: targetPanX,
    panY: targetPanY,
    zoom: Number(fitZoom.toFixed(2)),
  };
}
